import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { Auth } from '@freelancer/auth';
import type {
  BackendUpdateResponse,
  DatastoreDocument,
  RequestStatus,
} from '@freelancer/datastore';
import { Datastore } from '@freelancer/datastore';
import type {
  OperatingArea,
  OperatingAreasCollection,
  UsersSelfCollection,
} from '@freelancer/datastore/collections';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type { SelectorItem } from 'app/item-selector/item-selector.types';
import { SelectorButtonType } from 'app/item-selector/item-selector.types';
import type { Observable } from 'rxjs';
import { BehaviorSubject, combineLatest, firstValueFrom } from 'rxjs';
import { map, withLatestFrom } from 'rxjs/operators';

@UntilDestroy({ className: 'OperatingAreasFormComponent' })
@Component({
  standalone: false,
  selector: 'app-operating-areas-form',
  template: `
    @if ((updateUserOperatingAreasPromise | flAsync)?.status === 'error') {
      <fl-banner-alert
        class="mb-small"
        [type]="BannerAlertType.ERROR"
      >
        @if (
          (updateUserOperatingAreasPromise | flAsync)?.errorCode ===
          'OPERATING_AREA_NOT_FOUND'
        ) {
          <ng-container i18n="Operating areas error">
            Selected operating area does not exist.
          </ng-container>
        } @else {
          <ng-container i18n="Operating areas error">
            An error occurred while updating your operating areas.
          </ng-container>
        }
      </fl-banner-alert>
    }

    <app-item-selector
      i18n-itemCardHeading="Heading for the card that contains the items"
      i18n-itemName="Name of the items in the selector"
      i18n-formHeading="Item selector form heading"
      i18n-noSelectedHeading="Heading to show if there is no selected item"
      i18n-noSelectedMessage="Message to show if there is no selected item"
      itemCardHeading="States and Territories"
      itemName="Operating Area"
      formHeading="Select States and Territories you operate in"
      noSelectedHeading="No State and Territory selected"
      noSelectedMessage="Please select the states and territories you operate in
      so we can notify you on suitable jobs. Multiple selections allowed."
      [buttonType]="buttonType"
      [itemsList]="operatingAreasListItems$ | flAsync"
      [isBusySaving]="
        updateUserOperatingAreasPromise &&
        (updateUserOperatingAreasPromise | flAsync) === undefined
      "
      [footerStickyStatic]="footerStickyStatic"
      [footerFullWidth]="footerFullWidth"
      [pageStatus]="pageStatus$ | flAsync"
      [selectedItems]="selectedOperatingAreaItems$ | flAsync"
      [showBackButton]="showBackButton"
      (onDeselectItem)="handleDeselectOperatingArea($event)"
      (onSelectItem)="handleSelectOperatingArea($event)"
      (onBackButtonPressed)="handleBack()"
      (onSaveItems)="saveOperatingAreaChanges()"
    >
    </app-item-selector>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OperatingAreasFormComponent implements OnInit {
  BannerAlertType = BannerAlertType;

  @Input() buttonType: SelectorButtonType = SelectorButtonType.SAVE;
  @Input() footerStickyStatic = false;
  @Input() footerFullWidth = true;
  @Input() showBackButton = false;

  // Pre-selected operating states
  @Input() preSelectedOperatingStateCodes?: readonly string[];

  @Output() onSaveOperatingAreas = new EventEmitter<readonly OperatingArea[]>();
  @Output() onBackButtonPressed = new EventEmitter<void>();

  private selectedOperatingAreasSubject$: BehaviorSubject<
    readonly OperatingArea[]
  >;

  operatingAreasList$: Observable<readonly OperatingArea[]>;
  operatingAreasListItems$: Observable<readonly SelectorItem[]>;
  pageStatus$: Observable<RequestStatus<any>>;
  selectedOperatingAreas$: Observable<readonly OperatingArea[]>;
  selectedOperatingAreaItems$: Observable<readonly SelectorItem[]>;

  userSelfDocument: DatastoreDocument<UsersSelfCollection>;

  updateUserOperatingAreasPromise?: Promise<
    BackendUpdateResponse<UsersSelfCollection> | undefined
  >;

  constructor(
    private auth: Auth,
    private datastore: Datastore,
  ) {}

  ngOnInit(): void {
    const operatingAreasListCollection =
      this.datastore.collection<OperatingAreasCollection>('operatingAreas');

    this.operatingAreasList$ = operatingAreasListCollection.valueChanges();

    this.selectedOperatingAreasSubject$ = new BehaviorSubject<
      readonly OperatingArea[]
    >([]);
    this.selectedOperatingAreas$ =
      this.selectedOperatingAreasSubject$.asObservable();

    this.selectedOperatingAreaItems$ = this.selectedOperatingAreas$.pipe(
      map(selectedOperatingAreas => {
        return selectedOperatingAreas.map(operatingArea => {
          return {
            id: operatingArea.id,
            name: `${operatingArea.stateShortName} ${operatingArea.region}`,
            isSelected: true,
          };
        });
      }),
    );

    this.userSelfDocument = this.datastore.document<UsersSelfCollection>(
      'usersSelf',
      this.auth.getUserId(),
    );

    firstValueFrom(
      combineLatest([
        this.userSelfDocument.valueChanges(),
        this.operatingAreasList$,
      ]).pipe(
        map(([userSelf, operatingAreasList]) =>
          userSelf.operatingAreas
            ? userSelf.operatingAreas.filter(userOperatingArea =>
                operatingAreasList.find(
                  operatingArea => operatingArea.id === userOperatingArea.id,
                ),
              )
            : null,
        ),
        untilDestroyed(this),
      ),
    ).then(userValidOperatingAreas => {
      if (userValidOperatingAreas) {
        this.selectedOperatingAreasSubject$.next(userValidOperatingAreas);
      }
    });

    this.operatingAreasListItems$ = combineLatest([
      this.operatingAreasList$,
      this.selectedOperatingAreas$,
    ]).pipe(
      map(([operatingAreasList, selectedOperatingAreas]) => {
        return operatingAreasList.map(operatingArea => {
          return {
            id: operatingArea.id,
            name: `${operatingArea.stateShortName} ${operatingArea.region}`,
            isSelected:
              selectedOperatingAreas.filter(
                selectedOperatingArea =>
                  selectedOperatingArea.id === operatingArea.id,
              ).length > 0,
          };
        });
      }),
    );

    this.pageStatus$ = combineLatest([
      operatingAreasListCollection.status$,
      this.userSelfDocument.status$,
    ]).pipe(
      map(
        (statuses: readonly RequestStatus<any>[]) =>
          statuses.find(status => !status.ready || status.error) || {
            ready: true,
          },
      ),
    );

    if (this.preSelectedOperatingStateCodes) {
      firstValueFrom(this.operatingAreasList$.pipe(untilDestroyed(this))).then(
        operatingAreas => {
          const selectedOperatingStates: OperatingArea[] = [];

          operatingAreas.forEach(operatingArea => {
            const includeOperatingArea =
              this.preSelectedOperatingStateCodes?.includes(
                operatingArea.stateShortName || '',
              );
            if (includeOperatingArea) {
              selectedOperatingStates.push(operatingArea);
            }
          });

          this.selectedOperatingAreasSubject$.next(selectedOperatingStates);
        },
      );
    }
  }

  handleSelectOperatingArea(selectedItem: SelectorItem): void {
    firstValueFrom(
      this.selectedOperatingAreas$.pipe(
        withLatestFrom(this.operatingAreasList$),
        untilDestroyed(this),
      ),
    ).then(([selectedOperatingAreas, operatingAreasList]) => {
      this.selectedOperatingAreasSubject$.next([
        ...selectedOperatingAreas,
        // FIXME: T274808 There's no guarantee that this is defined
        operatingAreasList.find(
          operatingArea => operatingArea.id === selectedItem.id,
        ) as OperatingArea,
      ]);
    });
  }

  handleDeselectOperatingArea(deselectedItem: SelectorItem): void {
    firstValueFrom(
      this.selectedOperatingAreas$.pipe(untilDestroyed(this)),
    ).then(operatingAreas => {
      this.selectedOperatingAreasSubject$.next(
        operatingAreas.filter(
          operatingArea => operatingArea.id !== deselectedItem.id,
        ),
      );
    });
  }

  async saveOperatingAreaChanges(): Promise<void> {
    const selectedOperatingAreas = await firstValueFrom(
      this.selectedOperatingAreas$.pipe(untilDestroyed(this)),
    );

    this.updateUserOperatingAreasPromise = this.userSelfDocument.update({
      operatingAreas: selectedOperatingAreas,
    });

    const result = await this.updateUserOperatingAreasPromise;

    if (result?.status === 'success') {
      this.onSaveOperatingAreas.emit(selectedOperatingAreas);
    }
  }

  handleBack(): void {
    this.onBackButtonPressed.emit();
  }
}
